import type { components } from "@majitap/sdk";
import {
  Button,
  fieldErrorsOf,
  formatMoney,
  formatPhone,
  NoAccess,
  PageHeader,
  type SearchResult,
  SearchSelect,
  TextButton,
  useIdempotencyKeys,
  zonedInstant,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { DatePicker } from "@majitap/ui/arc/date-picker/date-picker";
import { Input } from "@majitap/ui/arc/input/input";
import { NumberField } from "@majitap/ui/arc/number-field/number-field";
import { RadioCards } from "@majitap/ui/arc/radio-cards/radio-cards";
import { SearchField } from "@majitap/ui/arc/search-field/search-field";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { TimePicker } from "@majitap/ui/arc/time-picker/time-picker";
import { useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { api, errorOf } from "../../api";
import { type Business, businessQuery, ROLES } from "../../businesses";
import { useProductItems } from "../../catalog";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { useDebounced } from "../../search";
import { meQuery } from "../../session";
import { PlaceDialog } from "../customers/places";
import styles from "./orders.module.css";

type Customer = components["schemas"]["Customer"];
type Quote = components["schemas"]["StaffQuote"];
interface Line {
  key: string;
  product_item_id: string;
  quantity: number;
}

const newLine = (): Line => ({ key: crypto.randomUUID(), product_item_id: "", quantity: 1 });

/**
 * Where the order can go, each saying who is billed: a customer's own places and their businesses'
 * places, or a business's places.
 */
function useDestinations(customer: Customer | null, business: Business | null) {
  return useQuery({
    queryKey: ["order-destinations", customer?.id ?? business?.id],
    enabled: customer !== null || business !== null,
    queryFn: async () => {
      const owners = business
        ? [{ business_id: business.id, billed: "Billed to the business" }]
        : customer
          ? [
              { customer_id: customer.id, billed: "The customer's own" },
              ...customer.businesses.map((b) => ({
                business_id: b.id,
                billed: `${b.business_name} (billed to the business)`,
              })),
            ]
          : [];
      const pages = await Promise.all(
        owners.map(async ({ billed, ...owner }) => {
          const { data, error } = await api.GET("/v1/admin/locations", { params: { query: { ...owner, limit: 100 } } });
          if (error || !data) throw errorOf(error);
          return data.data.map((l) => ({ ...l, billed }));
        }),
      );
      return pages.flat();
    },
  });
}

/**
 * The member a business's order is placed in the name of, by default: its first owner, else its
 * first manager, else anyone (the API's own default is its longest-standing owner).
 */
const defaultMember = (b: Business) =>
  (b.members.find((m) => m.role === "owner") ?? b.members.find((m) => m.role === "manager") ?? b.members[0])
    ?.customer_id ?? null;

/**
 * Place an order for a customer or a business (P8-07, `uOfeg` in the design). The server prices
 * it; this only shows the quote.
 */
export function NewOrderPage(): ReactNode {
  const { data: me } = useSuspenseQuery(meQuery);
  const format = useCompanyFormat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const keys = useIdempotencyKeys();
  const products = useProductItems();

  const search = useSearch({ from: "/console/orders/new" });
  const [mode, setMode] = useState<"customer" | "business">(search.business_id ? "business" : "customer");
  const [query, setQuery] = useState("");
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(search.business_id ?? null);
  // who a business's order is placed in the name of; null: the default member
  const [memberId, setMemberId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [placeQuery, setPlaceQuery] = useState("");
  const [lines, setLines] = useState<Line[]>([newLine()]);
  const [voucher, setVoucher] = useState("");
  const [day, setDay] = useState<Date | undefined>();
  const [time, setTime] = useState("08:00");
  const [lpo, setLpo] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string>();
  const [placing, setPlacing] = useState(false);
  // a new place for the customer, without leaving the order (P8-41)
  const [addingPlace, setAddingPlace] = useState(false);
  const canAddPlace = me?.permissions.includes("customers.manage") ?? false;

  const q = useDebounced(query.trim());
  const found = useQuery({
    queryKey: ["customer-search", q],
    enabled: q.length >= 2,
    queryFn: async () => {
      const { data, error } = await api.GET("/v1/admin/customers", { params: { query: { q, limit: 8 } } });
      if (error || !data) throw errorOf(error);
      return data.data;
    },
  });
  const foundBusinesses = useQuery({
    queryKey: ["business-search", q],
    enabled: mode === "business" && q.length >= 2,
    queryFn: async () => {
      const { data, error } = await api.GET("/v1/admin/businesses", { params: { query: { q, limit: 8 } } });
      if (error || !data) throw errorOf(error);
      return data.data;
    },
  });
  const businessRecord = useQuery({ ...businessQuery(businessId ?? ""), enabled: mode === "business" && !!businessId });
  const business = mode === "business" ? (businessRecord.data ?? null) : null;
  const member = business ? (memberId ?? defaultMember(business)) : null;
  const destinations = useDestinations(mode === "customer" ? customer : null, business);

  const items = lines
    .filter((l) => l.product_item_id && l.quantity > 0)
    .map((l) => ({ product_item_id: l.product_item_id, quantity: String(l.quantity) }));
  const voucherCode = useDebounced(voucher.trim(), 500);
  // a business's order is priced for the member it's placed for, as the server will price it
  const payer = business
    ? member
      ? { customer_id: member, business_id: business.id }
      : null
    : customer
      ? { customer_id: customer.id }
      : null;
  const quoteBody =
    payer && locationId && items.length > 0
      ? { ...payer, location_id: locationId, items, voucher_code: voucherCode || undefined }
      : null;
  const quote = useQuery({
    queryKey: ["quote", quoteBody],
    enabled: quoteBody !== null,
    retry: false,
    queryFn: async (): Promise<Quote> => {
      const { data, error } = await api.POST("/v1/admin/pricing/quote", { body: quoteBody! });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });

  if (!me?.permissions.includes("orders.write")) {
    return (
      <>
        <PageHeader title="New order" />
        <NoAccess description="Placing orders needs the orders.write permission. Ask an administrator if you need it." />
      </>
    );
  }

  const chooseCustomer = (result: SearchResult | null) => {
    setCustomer(result ? (found.data?.find((c) => c.id === result.id) ?? null) : null);
    setLocationId(null);
    setPlaceQuery("");
  };
  const chooseBusiness = (result: SearchResult | null) => {
    setBusinessId(result?.id ?? null);
    setMemberId(null);
    setLocationId(null);
    setPlaceQuery("");
  };
  const chooseMode = (next: string) => {
    setMode(next as "customer" | "business");
    setQuery("");
    setLocationId(null);
    setPlaceQuery("");
  };
  const owner = business ?? customer;
  // the places matching the search, by name, address or who is billed; the chosen one stays listed
  const placeTerms = placeQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const places = (destinations.data ?? []).filter(
    (l) =>
      l.id === locationId ||
      placeTerms.every((t) => [l.label, l.address_line, l.billed].join(" ").toLowerCase().includes(t)),
  );
  const setLine = (key: string, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const place = async (event: FormEvent) => {
    event.preventDefault();
    if (!quoteBody || !quote.data) return;
    const body = {
      ...quoteBody,
      expected_total: quote.data.receivable_total,
      scheduled_for: day ? zonedInstant(day, time, format.timezone) : undefined,
      lpo_reference: lpo.trim() || undefined,
      notes: notes.trim() || undefined,
    };
    setPlacing(true);
    setProblem(undefined);
    setErrors({});
    const { data, error } = await api.POST("/v1/admin/orders", {
      body,
      params: { header: { "idempotency-key": keys.keyFor(body) } },
    });
    setPlacing(false);
    if (data) {
      keys.settled();
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await navigate({ to: "/orders/$orderId", params: { orderId: data.id } });
      return;
    }
    const body_ = errorOf(error);
    keys.settled();
    if (body_.code === "PRICE_CHANGED") {
      const fresh = (body_.details as { quote?: Quote } | undefined)?.quote;
      if (fresh) queryClient.setQueryData(["quote", quoteBody], fresh);
      setProblem("The price changed while you were placing the order. Check the new total, then place it again.");
    } else {
      setErrors(fieldErrorsOf(body_));
      setProblem(messageFor(body_));
    }
  };

  const receivable = (quote.data?.deliveries ?? []).flatMap((d, i, all) =>
    d.lines
      .filter((l) => l.direction === "receivable")
      .map((l) => ({
        label: `${all.length > 1 ? `Delivery ${i + 1} · ` : ""}${l.product_item_id ? products.labelOf(l.product_item_id) : l.charge_type.name}`,
        amount: l.amount,
      })),
  );

  return (
    <>
      <PageHeader
        title="New order"
        description="Place an order for a customer or a business. MajiTap's price rules set the price."
      />
      <form onSubmit={place} noValidate className={styles.columns}>
        <div className={styles.form}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Order for</h2>
            <SegmentedControl
              label="Order for"
              value={mode}
              onValueChange={chooseMode}
              options={[
                { value: "customer", label: "A customer" },
                { value: "business", label: "A business" },
              ]}
            />
            {mode === "customer" ? (
              <SearchSelect
                label="Customer"
                placeholder="Search by name or phone number"
                value={
                  customer
                    ? {
                        id: customer.id,
                        label: customer.user.name,
                        description: [
                          formatPhone(customer.user.phone_number),
                          ...customer.businesses.map((b) => b.business_name),
                        ]
                          .filter(Boolean)
                          .join(" · "),
                      }
                    : null
                }
                onChange={chooseCustomer}
                query={query}
                onQueryChange={setQuery}
                pending={query.trim() !== q || found.isFetching}
                results={(found.data ?? []).map((c) => ({
                  id: c.id,
                  label: c.user.name,
                  description: [formatPhone(c.user.phone_number), c.user.email].filter(Boolean).join(" · "),
                }))}
                error={errors.customer_id}
              />
            ) : (
              <>
                <SearchSelect
                  label="Business"
                  placeholder="Search by name"
                  value={
                    business
                      ? { id: business.id, label: business.business_name, description: business.tax_pin ?? undefined }
                      : null
                  }
                  onChange={chooseBusiness}
                  query={query}
                  onQueryChange={setQuery}
                  pending={query.trim() !== q || foundBusinesses.isFetching}
                  results={(foundBusinesses.data ?? []).map((b) => ({
                    id: b.id,
                    label: b.business_name,
                    description: b.tax_pin ?? undefined,
                  }))}
                  error={errors.business_id}
                />
                {business && business.members.length === 0 ? (
                  <Alert tone="warning" title="No members yet">
                    An order is placed in a member's name. Add one on the business's page first.
                  </Alert>
                ) : business ? (
                  <Select
                    label="Placed in the name of"
                    description="The business is billed and the order is in this member's name."
                    value={member ?? ""}
                    onValueChange={setMemberId}
                    options={business.members.map((m) => ({
                      value: m.customer_id,
                      label: [m.name, ROLES[m.role].label, formatPhone(m.phone_number), m.email]
                        .filter(Boolean)
                        .join(" · "),
                    }))}
                  />
                ) : null}
              </>
            )}
          </section>

          {owner ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Deliver to</h2>
              {destinations.data && destinations.data.length === 0 ? (
                <Alert tone="warning" title="No places yet">
                  {canAddPlace
                    ? `This ${business ? "business" : "customer"} has no location yet. Add one: search its address.`
                    : `This ${business ? "business" : "customer"} has no location. Someone who manages customers can add one on their page.`}
                </Alert>
              ) : (
                <>
                  <SearchField
                    label="Search places"
                    value={placeQuery}
                    onValueChange={setPlaceQuery}
                    placeholder="Place name or address"
                  />
                  {places.length === 0 && destinations.data ? (
                    <p className={styles.muted}>No place matches “{placeQuery.trim()}”.</p>
                  ) : null}
                  <RadioCards
                    layout="list"
                    aria-label="Deliver to"
                    value={locationId}
                    onValueChange={setLocationId}
                    options={places.map((l) => ({
                      value: l.id,
                      label: l.label,
                      description: [l.address_line, l.billed].filter(Boolean).join(" · "),
                    }))}
                  />
                </>
              )}
              {canAddPlace ? (
                <div>
                  <TextButton onClick={() => setAddingPlace(true)}>Add a place</TextButton>
                </div>
              ) : null}
              {addingPlace ? (
                <PlaceDialog
                  owner={business ? { business_id: business.id } : { customer_id: owner.id }}
                  onClose={async () => {
                    setAddingPlace(false);
                    await queryClient.invalidateQueries({ queryKey: ["order-destinations", owner.id] });
                  }}
                />
              ) : null}
            </section>
          ) : null}

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Items</h2>
            {lines.map((line, i) => (
              <div key={line.key} className={styles.row}>
                <Select
                  label={i === 0 ? "Product" : `Product ${i + 1}`}
                  placeholder="Choose a product"
                  value={line.product_item_id}
                  onValueChange={(v) => setLine(line.key, { product_item_id: v })}
                  options={products.items.filter((p) => p.orderable).map((p) => ({ value: p.id, label: p.label }))}
                />
                <NumberField
                  label="Quantity"
                  min={1}
                  max={999}
                  value={line.quantity}
                  onValueChange={(v) => setLine(line.key, { quantity: v })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  aria-label="Remove this item"
                  disabled={lines.length === 1}
                  onClick={() => setLines((ls) => ls.filter((l) => l.key !== line.key))}
                >
                  <Trash2 size={18} aria-hidden="true" />
                </Button>
              </div>
            ))}
            {lines.length < 20 ? (
              <TextButton onClick={() => setLines((ls) => [...ls, newLine()])}>Add an item</TextButton>
            ) : null}
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Details (optional)</h2>
            <div className={styles.pair}>
              <Input
                label="Voucher code"
                value={voucher}
                onChange={(e) => setVoucher(e.target.value)}
                description="Applied when the order is priced."
                error={errors.voucher_code}
              />
              <DatePicker
                label="Deliver on"
                value={day}
                onChange={setDay}
                minDate={new Date()}
                placeholder="As soon as possible"
              />
              {day ? <TimePicker label="At" value={time} onChange={setTime} format="24h" minuteStep={30} /> : null}
            </div>
            <Input
              label="LPO reference"
              value={lpo}
              onChange={(e) => setLpo(e.target.value)}
              error={errors.lpo_reference}
            />
            <Textarea
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              error={errors.notes}
            />
          </section>
        </div>

        <aside className={styles.quote} aria-live="polite">
          <h2 className={styles.sectionTitle}>Price</h2>
          {quoteBody === null ? (
            <p className={styles.muted}>Choose who it's for, a place and at least one item to see the price.</p>
          ) : quote.isPending ? (
            <p className={styles.muted}>Pricing…</p>
          ) : quote.isError ? (
            <Alert tone="warning" title="This order can't be priced">
              {messageFor(errorOf(quote.error))}
            </Alert>
          ) : (
            <>
              {receivable.map((l) => (
                <div key={`${l.label}-${l.amount}`} className={styles.line}>
                  <span>{l.label}</span>
                  <span>{formatMoney(l.amount, format)}</span>
                </div>
              ))}
              <div className={styles.total}>
                <span>Total</span>
                <span>{formatMoney(quote.data.receivable_total, format)}</span>
              </div>
              <p className={styles.muted}>
                {quote.data.deliveries.length === 1 ? "1 delivery" : `${quote.data.deliveries.length} deliveries`}
              </p>
            </>
          )}
          {problem ? (
            <Alert
              tone="danger"
              title={problem.startsWith("The price changed") ? "The price changed" : "The order wasn't placed"}
            >
              {problem}
            </Alert>
          ) : null}
          <Button type="submit" loading={placing} disabled={!quote.data || quote.isFetching}>
            Place order
          </Button>
          <p className={styles.muted}>
            MajiTap prices the order again when you place it. If the price has changed, you'll see the new one before
            anything is placed.
          </p>
        </aside>
      </form>
    </>
  );
}
